import { useCallback, useEffect, useRef, useState, type DependencyList } from 'react';
import type { PageWindow } from '@gr/core/paging';
import type { OperationResult } from '@gr/utils/operation-result';

export interface Resource<T> {
  data: T | null;
  loading: boolean;
  /** Коды ошибок последней неудачной загрузки. */
  error: string[] | null;
  reload(): Promise<void>;
  /** Новые данные или функция от текущих — для правок без перечитывания, со стабильным колбэком. */
  setData(data: T | ((current: T | null) => T | null)): void;
}

/** Загрузка данных с состояниями загрузки и ошибки; reload — повтор без перезагрузки страницы. */
export function useResource<T>(load: () => Promise<OperationResult<T>>, deps: DependencyList): Resource<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string[] | null>(null);
  const generation = useRef(0);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const run = useCallback(load, deps);

  const reload = useCallback(async () => {
    const current = ++generation.current;
    setLoading(true);
    const result = await run();
    if (current !== generation.current) return;
    if (result.success) {
      setData(result.data);
      setError(null);
    } else {
      setError(result.messages);
    }
    setLoading(false);
  }, [run]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { data, loading, error, reload, setData };
}

export interface Action<A extends unknown[], T> {
  run(...args: A): Promise<OperationResult<T>>;
  pending: boolean;
  error: string[] | null;
  fieldErrors: Record<string, string>;
  reset(): void;
}

/** Действие пользователя: блокировка на время выполнения, ошибки по полям. */
export function useAction<A extends unknown[], T>(action: (...args: A) => Promise<OperationResult<T>>): Action<A, T> {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string[] | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  // Флаг ставится в теле эффекта, а не только снимается в очистке: StrictMode в режиме разработки
  // монтирует компонент дважды (монтирование → очистка → монтирование), и флаг, снятый очисткой,
  // иначе так и остался бы false — pending не сбрасывался бы, ошибки не показывались.
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const run = useCallback(
    async (...args: A) => {
      setPending(true);
      setError(null);
      setFieldErrors({});
      const result = await action(...args);
      if (mounted.current) {
        setPending(false);
        if (!result.success) {
          setError(result.messages);
          setFieldErrors(result.fieldErrors ?? {});
        }
      }
      return result;
    },
    [action],
  );

  const reset = useCallback(() => {
    setError(null);
    setFieldErrors({});
  }, []);

  return { run, pending, error, fieldErrors, reset };
}

/** Как устроена страница списка: сколько в ней записей, как взять первые N и дописать следующую. */
export interface Paging<P> {
  count(page: P): number;
  take(page: P, size: number): P;
  append(loaded: P, next: P): P;
}

export function arrayPaging<T>(): Paging<T[]> {
  return { count: (page) => page.length, take: (page, size) => page.slice(0, size), append: (loaded, next) => [...loaded, ...next] };
}

export interface PagedResource<P> extends Resource<P> {
  hasMore: boolean;
  loadingMore: boolean;
  loadMore(): Promise<void>;
}

/**
 * Список страницами (docs/scaling.md): запрашивается на одну запись больше страницы —
 * так видно, есть ли продолжение. reload и смена фильтров возвращают к первой странице.
 */
export function usePagedResource<P>(load: (page: PageWindow) => Promise<OperationResult<P>>, deps: DependencyList, paging: Paging<P>, size: number): PagedResource<P> {
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const loaded = useRef(0);
  const generation = useRef(0);

  const resource = useResource(async () => {
    const current = ++generation.current;
    const result = await load({ limit: size + 1, offset: 0 });
    if (!result.success || current !== generation.current) return result;
    const first = paging.take(result.data, size);
    loaded.current = paging.count(first);
    setHasMore(paging.count(result.data) > size);
    return { ...result, data: first };
  }, deps);

  const loadMore = useCallback(async () => {
    if (!resource.data) return;
    const current = generation.current;
    setLoadingMore(true);
    const result = await load({ limit: size + 1, offset: loaded.current });
    setLoadingMore(false);
    if (!result.success || current !== generation.current) return;
    const next = paging.take(result.data, size);
    loaded.current += paging.count(next);
    setHasMore(paging.count(result.data) > size);
    resource.setData(paging.append(resource.data, next));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resource.data, ...deps]);

  return { ...resource, hasMore, loadingMore, loadMore };
}
